import { Link } from 'react-router-dom';
import { CheckCircle, XCircle, HelpCircle, Mail, Send, Clock, ArrowRight } from 'lucide-react';
import type { ApplicationStatusHistory, EmailThread, CTAAction } from '@core/types';
import { StatusBadge } from '@shared/components/ui/StatusBadge';
import { CandidateCollapsiblePanel } from '@modules/candidates/CandidateCollapsiblePanel';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';

const CTA_ICON: Record<CTAAction, React.ReactNode> = {
  accept: <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />,
  reject: <XCircle className="w-3.5 h-3.5 text-red-400" />,
  query:  <HelpCircle className="w-3.5 h-3.5 text-amber-400" />,
};
const CTA_LABEL: Record<CTAAction, string> = { accept: 'Shortlisted', reject: 'Passed', query: 'Query' };

export type HeartbeatTimelineItem =
  | { type: 'status'; date: string; entry: ApplicationStatusHistory }
  | { type: 'email_sent'; date: string; thread: EmailThread }
  | { type: 'client_responded'; date: string; thread: EmailThread }
  | { type: 'next_action'; label: string; action: 'send_email' | 'reply'; threadId?: string };

function formatTimelineDate(date: string) {
  return formatDateTimeGmt(date);
}

/** Same pipeline + email merge as Application detail overview sidebar (heartbeat = next action dot). */
export function buildApplicationHeartbeatTimeline(
  history: ApplicationStatusHistory[],
  emailThreads: EmailThread[]
): { timeline: HeartbeatTimelineItem[]; nextActionIndex: number } {
  const items: HeartbeatTimelineItem[] = [];
  history.forEach((entry) => {
    items.push({ type: 'status', date: entry.changed_at, entry });
  });
  emailThreads.forEach((thread) => {
    if (thread.sent_at) {
      items.push({ type: 'email_sent', date: thread.sent_at, thread });
    }
    if (thread.cta_responded_at) {
      items.push({ type: 'client_responded', date: thread.cta_responded_at, thread });
    }
  });
  items.sort((a, b) => {
    const dA = a.type === 'next_action' ? 0 : new Date(a.date).getTime();
    const dB = b.type === 'next_action' ? 1 : new Date(b.date).getTime();
    return dA - dB;
  });
  const hasAwaitingReply = emailThreads.some((t) => t.sent_at && !t.cta_response);
  const hasNoEmails = emailThreads.length === 0;
  let nextIdx = -1;
  if (hasAwaitingReply) {
    const latestPending = [...emailThreads]
      .filter((t) => t.sent_at && !t.cta_response)
      .sort((a, b) => new Date(b.sent_at!).getTime() - new Date(a.sent_at!).getTime())[0];
    if (latestPending) {
      const idx = items.findIndex((i) => i.type === 'email_sent' && i.thread.id === latestPending.id);
      nextIdx = idx >= 0 ? idx : items.length;
    }
  } else if (hasNoEmails) {
    items.push({ type: 'next_action', label: 'Send first email to client', action: 'send_email' });
    nextIdx = items.length - 1;
  } else {
    const lastThread = [...emailThreads].sort(
      (a, b) => new Date(b.sent_at ?? 0).getTime() - new Date(a.sent_at ?? 0).getTime()
    )[0];
    if (lastThread?.cta_response === 'query') {
      items.push({ type: 'next_action', label: 'Reply to client query', action: 'reply', threadId: lastThread.id });
      const queryRespIdx = items.findIndex(
        (i) => i.type === 'client_responded' && i.thread.id === lastThread.id
      );
      nextIdx = queryRespIdx >= 0 ? queryRespIdx : items.length - 1;
    }
  }
  return { timeline: items, nextActionIndex: nextIdx };
}

export function ApplicationHeartbeatTimeline(props: {
  applicationId: string;
  timeline: HeartbeatTimelineItem[];
  nextActionIndex: number;
}) {
  const { applicationId, timeline, nextActionIndex } = props;

  return (
    <CandidateCollapsiblePanel storageKey={`app-${applicationId}-timeline`} title="Timeline" defaultOpen>
      <div className="px-1">
      <Link
        to={`/emails/compose?applicationId=${applicationId}`}
        className="flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-white transition-colors w-full mb-4"
        style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
      >
        <Send className="w-3.5 h-3.5" /> Send Email
      </Link>
      {timeline.length === 0 ? (
        <div className="text-center py-4">
          <Mail className="w-8 h-8 vscode-text-tertiary mx-auto mb-2 opacity-50" />
          <p className="text-xs vscode-text-tertiary">No activity yet.</p>
          <p className="text-xs vscode-text-tertiary">Send an email to start.</p>
        </div>
      ) : (
        <div className="relative">
          {timeline.map((item, idx) => {
            const isHeartbeat = idx === nextActionIndex;
            const dotColor = item.type === 'status' 
              ? 'rgba(139, 92, 246, 0.8)' 
              : item.type === 'email_sent' 
                ? 'rgba(59, 130, 246, 0.8)'
                : item.type === 'client_responded'
                  ? item.thread.cta_response === 'accept' ? 'rgba(16, 185, 129, 0.8)' : item.thread.cta_response === 'reject' ? 'rgba(239, 68, 68, 0.8)' : 'rgba(245, 158, 11, 0.8)'
                  : 'rgba(16, 185, 129, 0.8)';
            return (
              <div
                key={
                  item.type === 'status'
                    ? item.entry.id
                    : item.type === 'next_action'
                      ? `next-${idx}`
                      : item.thread.id + (item.type === 'client_responded' ? '-resp' : '')
                }
                className="flex gap-3"
              >
                {/* Dot + vertical line */}
                <div className="flex flex-col items-center">
                  {isHeartbeat ? (
                    <div
                      className="w-3.5 h-3.5 rounded-full flex-shrink-0 animate-pulse ring-2 ring-emerald-400/30"
                      style={{ background: '#10b981' }}
                    />
                  ) : (
                    <div 
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-0.5"
                      style={{ background: dotColor }}
                    />
                  )}
                  {idx < timeline.length - 1 && (
                    <div className="w-px flex-1 min-h-[16px] mt-1"
                      style={{ background: 'var(--vscode-border-primary)' }} />
                  )}
                </div>

                {/* Content */}
                <div
                  className={`pb-3 min-w-0 flex-1 ${isHeartbeat ? 'rounded-lg px-3 py-2 -mx-1' : ''}`}
                  style={isHeartbeat ? { background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)' } : {}}
                >
                  {item.type === 'status' && (
                    <>
                      <div className="flex items-center gap-2 flex-wrap">
                        {item.entry.from_status && (
                          <>
                            <StatusBadge status={item.entry.from_status} />
                            <ArrowRight className="w-3 h-3 vscode-text-tertiary" />
                          </>
                        )}
                        <StatusBadge status={item.entry.to_status} />
                      </div>
                      <p className="text-[10px] vscode-text-tertiary mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatTimelineDate(item.entry.changed_at)}
                      </p>
                      {item.entry.note && (
                        <p className="text-xs vscode-text-secondary mt-1 italic bg-gray-800/30 rounded px-2 py-1">&quot;{item.entry.note}&quot;</p>
                      )}
                    </>
                  )}
                  {item.type === 'email_sent' && (
                    <Link to={`/emails/thread/${item.thread.id}`} className="block hover:opacity-80 group">
                      <div className="flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 text-blue-400" />
                        <span className="text-sm vscode-text-primary font-medium group-hover:text-blue-400">Email sent</span>
                      </div>
                      <p className="text-[10px] vscode-text-tertiary mt-0.5">
                        To: {item.thread.recipient_name || item.thread.recipient_email}
                      </p>
                      <p className="text-[10px] vscode-text-tertiary mt-0.5 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatTimelineDate(item.thread.sent_at!)}
                      </p>
                      {isHeartbeat && (
                        <p className="text-xs mt-1.5 font-medium text-emerald-400 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Awaiting reply
                        </p>
                      )}
                    </Link>
                  )}
                  {item.type === 'client_responded' && (
                    <div className="rounded-lg vscode-bg-tertiary px-3 py-2">
                      <div className="flex items-center gap-2 text-sm">
                        {CTA_ICON[item.thread.cta_response!]}
                        <span className="vscode-text-primary font-medium">{CTA_LABEL[item.thread.cta_response!]}</span>
                      </div>
                      <p className="text-[10px] vscode-text-tertiary mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatTimelineDate(item.thread.cta_responded_at!)}
                      </p>
                      {item.thread.cta_response_note && (
                        <p className="text-xs vscode-text-secondary mt-1.5 italic">&quot;{item.thread.cta_response_note}&quot;</p>
                      )}
                    </div>
                  )}
                  {item.type === 'next_action' && (
                    <Link
                      to={
                        item.action === 'send_email'
                          ? `/emails/compose?applicationId=${applicationId}`
                          : `/emails/compose?applicationId=${applicationId}${item.threadId ? `&replyToThread=${item.threadId}` : ''}`
                      }
                      className="block group"
                    >
                      <p className="text-sm font-medium text-emerald-400 group-hover:text-emerald-300 flex items-center gap-1">
                        <Send className="w-3.5 h-3.5" /> {item.label}
                      </p>
                      <p className="text-[10px] vscode-text-tertiary mt-0.5">Click to take action</p>
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </CandidateCollapsiblePanel>
  );
}
